import { useFleetStore } from '@/stores/fleet'
import { useMomentaryStore } from '@/stores/momentary'
import { myAxios, myAxiosRetry } from '@/services/myAxios'
import { useDeviceStore } from '@/stores/device'
import { useRoutesStore } from '@/stores/routes'
import { useLogbookStore } from '@/stores/logbook'
import { AIS_CONFIG } from '@/utils/build-config'
import dayjs from 'dayjs'
import { loadJSON } from '@/utils/utils'

// Инициализируем хранилища Pinia
const getStores = () => {
  return {
    momentaryStore: useMomentaryStore(),
    fleetStore: useFleetStore(),
    deviceStore: useDeviceStore(),
    routesStore: useRoutesStore(),
    logbookStore: useLogbookStore(),
  }
}

// Хелпер для API-запросов
const makeApiRequest = async (url, method = 'get', data = null, customAxios = myAxios) => {
  const { momentaryStore } = getStores()

  momentaryStore.setBusy(true)
  momentaryStore.setAPIError(false)

  try {
    const response = method === 'get' ? await customAxios.get(url) : await customAxios.post(url, data)

    momentaryStore.setBusy(false)
    return response.data
  } catch (error) {
    momentaryStore.setBusy(false)
    momentaryStore.setAPIError(true)
    throw error
  }
}

// Загружает данные кораблей (инсталляции)
export async function loadFleetInstallations(forceRefresh = false) {
  const { fleetStore } = getStores()

  // Проверяем, есть ли уже данные и не запрошено ли принудительное обновление
  if (!forceRefresh && fleetStore.hasInstallations) {
    console.log('Using cached installations data')
    return fleetStore.installations
  }

  try {
    const data = await makeApiRequest('api/v1/installations')

    const instECDIS = data.userInfos.filter(e => e.applicationId === 11 || e.applicationId === 101 || e.applicationId === 102 || e.applicationId === 105)

    if (instECDIS.length > 0) {
      fleetStore.addInstallations(instECDIS)
    }

    return fleetStore.installations
  } catch (error) {
    console.error('Failed to load fleet installations:', error)
    throw error
  }
}

// Загружает договоры для кораблей
export async function loadFleetAgreements(forceRefresh = false) {
  const { fleetStore } = getStores()

  // Проверяем, есть ли уже данные и не запрошено ли принудительное обновление
  if (!forceRefresh && fleetStore.hasAgreements) {
    console.log('Using cached agreements data')
    return fleetStore.agreements
  }

  try {
    const data = await makeApiRequest('api/v2/agreement')
    fleetStore.addAgreements(data)
    return fleetStore.agreements
  } catch (error) {
    console.error('Failed to load fleet agreements:', error)
    throw error
  }
}

// Загружает картинку для регистрации в телеграм
export async function loadQrServiceCode() {
  const { momentaryStore } = getStores()
  momentaryStore.setBusy(true)
  momentaryStore.setAPIError(false)
  try {
    // Используем myAxios напрямую, чтобы указать responseType: 'blob' для обработки изображения
    const response = await myAxios.get('api/v1/login/qr', { responseType: 'blob' })
    momentaryStore.setBusy(false)
    // Создаем URL для Blob, чтобы использовать его в <img>
    return URL.createObjectURL(response.data)
  } catch (error) {
    momentaryStore.setBusy(false)
    momentaryStore.setAPIError(true)
    console.error('Failed to load qr code:', error)
    return null
  }
}

// Загружает купленные карты (фолио) для корабля по deviceId
export async function loadFleetFolio(deviceId, forceRefresh = false) {
  const { fleetStore } = getStores()

  // Проверяем, есть ли уже данные и не запрошено ли принудительное обновление
  if (!forceRefresh && fleetStore.hasFolio) {
    console.log('Using cached folio data')
    return fleetStore.folio
  }

  try {
    const data = await makeApiRequest(`api/v1/orders/deviceid/${deviceId}`, 'get', null, myAxiosRetry)
    fleetStore.addFolio(data.orders)
    return fleetStore.folio
  } catch (error) {
    console.error('Failed to load fleet folio:', error)
    return fleetStore.folio
  }
}

// Обогащает данные AIS из локального кэша
async function enrichFleetDataWithCache(fleetData, useCache = true) {
  if (!useCache || !fleetData || !Array.isArray(fleetData)) {
    return fleetData
  }

  console.log('Enriching fleet data with local AIS cache...')
  
  // Получаем доступ к installations данным для поиска IMO по deviceId
  const { fleetStore } = getStores()
  
  const enrichedData = await Promise.all(
    fleetData.map(async (item) => {
      try {
        // Проверяем есть ли данные position
        const hasValidPositionData = item?.position?.data && 
                                   typeof item.position.data === 'object' && 
                                   item.position.data !== ""

        // Извлекаем IMO из данных флота (может быть в payload или попытаемся найти другим способом)
        let imo = null
        if (hasValidPositionData && item.position.data.payload?.ImoNumber) {
          imo = item.position.data.payload.ImoNumber
        }

        // Если IMO не найден, попытаемся найти его по deviceId в installations данных
        if (!imo && item.deviceId && fleetStore.installations.length > 0) {
          const installation = fleetStore.installations.find(inst => inst.deviceId === item.deviceId)
          if (installation && installation.imo) {
            imo = installation.imo
          }
        }
        
        // Если IMO все еще не найден, попытаемся найти его в данных флота
        if (!imo && item.deviceId && fleetStore.getFleet && fleetStore.getFleet.length > 0) {
          const fleetShip = fleetStore.getFleet.find(ship => ship.ship?.deviceId === item.deviceId)
          if (fleetShip && fleetShip.ship && fleetShip.ship.imo) {
            imo = fleetShip.ship.imo
          }
        }
        
        if (!imo) {
          return item
        }

        // Загружаем данные из локального кэша
        // Проверяем, не содержит ли IMO уже префикс
        const imoStr = String(imo).startsWith('IMO') ? String(imo) : `IMO${imo}`
        const cacheUrl = `/ais/cache/${imoStr}/ship_data.json`
        const cacheResponse = await fetch(cacheUrl)
        
        if (!cacheResponse.ok) {
          console.warn(`No cache data found for IMO ${imo}`)
          return item
        }

        const cacheData = await cacheResponse.json()
        console.log(`Enriching data for IMO ${imo} from cache, has valid position data:`, hasValidPositionData)

        // Создаем обогащенную копию элемента
        const enrichedItem = { ...item }

        // Если нет данных AIS от API или они пустые - создаем полную структуру из кэша
        if (!hasValidPositionData) {
          console.log(`Creating complete AIS data structure from cache for IMO ${imo}`)
          
          if (!cacheData.position) {
            console.warn(`Cache data for IMO ${imo} has no position data, skipping`)
            return item
          }
          
          // Создаем полную структуру данных из кэша
          enrichedItem.position = {
            data: {
              lat: cacheData.position.latitude,
              lon: cacheData.position.longitude,
              time: cacheData.position.timestamp,
              speedKnots: parseFloat(cacheData.position.speed) || 0,
              destination: cacheData.destination || 'Unknown',
              eta: null, // будет заполнено ниже
              payload: {
                Mmsi: cacheData.mmsi || '',
                ImoNumber: cacheData.imo.replace('IMO', '') || '',
                VesselName: cacheData.name || 'Unknown',
                CallSign: cacheData.callsign || '',
                Position: {
                  lat: cacheData.position.latitude,
                  lon: cacheData.position.longitude
                },
                SpeedOverGround: {
                  Knots: parseFloat(cacheData.position.speed) || 0,
                  Ms: (parseFloat(cacheData.position.speed) || 0) * 0.514444,
                  Kph: (parseFloat(cacheData.position.speed) || 0) * 1.852,
                  Mph: (parseFloat(cacheData.position.speed) || 0) * 1.150779
                },
                CourseOverGround: parseFloat(cacheData.position.course) || 0,
                NavigationStatus: cacheData.navigational_status === '-' ? 'unknown' : (cacheData.navigational_status || 'unknown'),
                Destination: cacheData.destination || 'Unknown',
                DateReceived: cacheData.position.timestamp,
                // Остальные поля помечаем как OLD или заполняем из кэша
                Dte: 'OLD',
                EtaDay: 'OLD',
                Draught: cacheData.current_draught || 'OLD',
                EtaHour: 'OLD',
                EtaMonth: 'OLD',
                RaimFlag: 'OLD',
                ShipType: cacheData.vessel_type || 'OLD',
                EtaMinute: 'OLD',
                AisVersion: 'OLD',
                RateOfTurn: 'OLD',
                MessageType: 'OLD',
                TrueHeading: 'OLD',
                DimensionToBow: 'OLD',
                DimensionToPort: 'OLD',
                PositionFixType: 'OLD',
                DimensionToStern: 'OLD',
                PositionAccuracy: 'OLD',
                ManeuverIndicator: 'OLD',
                DimensionToStarboard: 'OLD'
              }
            },
            serverTimestamp: new Date().toISOString()
          }
        } else {
          // Если данные есть - обновляем их как раньше
          if (cacheData.position) {
            // Обновляем основные координаты
            enrichedItem.position.data.lat = cacheData.position.latitude
            enrichedItem.position.data.lon = cacheData.position.longitude
            enrichedItem.position.data.speedKnots = parseFloat(cacheData.position.speed) || 0
            
            // Обновляем destination
            enrichedItem.position.data.destination = cacheData.destination || 'Unknown'

            // Обновляем payload если он существует
            if (enrichedItem.position.data.payload) {
              // Обновляем Position в payload
              if (enrichedItem.position.data.payload.Position) {
                enrichedItem.position.data.payload.Position.lat = cacheData.position.latitude
                enrichedItem.position.data.payload.Position.lon = cacheData.position.longitude
              }

              // Обновляем скорость в payload
              const speedKnots = parseFloat(cacheData.position.speed) || 0
              if (enrichedItem.position.data.payload.SpeedOverGround) {
                enrichedItem.position.data.payload.SpeedOverGround.Knots = speedKnots
                enrichedItem.position.data.payload.SpeedOverGround.Ms = speedKnots * 0.514444
                enrichedItem.position.data.payload.SpeedOverGround.Kph = speedKnots * 1.852
                enrichedItem.position.data.payload.SpeedOverGround.Mph = speedKnots * 1.150779
              }

              // Обновляем курс
              const course = parseFloat(cacheData.position.course) || 0
              enrichedItem.position.data.payload.CourseOverGround = course

              // Обновляем NavigationStatus (из кода в текст)
              enrichedItem.position.data.payload.NavigationStatus = cacheData.navigational_status === '-' ? 'unknown' : (cacheData.navigational_status || 'unknown')

              // Обновляем destination в payload
              enrichedItem.position.data.payload.Destination = cacheData.destination || 'Unknown'

              // Помечаем остальные поля как устаревшие
              const fieldsToMarkOld = [
                'Dte', 'EtaDay', 'Draught', 'EtaHour', 'CallSign', 'EtaMonth', 
                'RaimFlag', 'ShipType', 'EtaMinute', 'AisVersion', 'RateOfTurn', 
                'VesselName', 'MessageType', 'TrueHeading', 'DateReceived', 
                'DimensionToBow', 'DimensionToPort', 'PositionFixType', 
                'DimensionToStern', 'PositionAccuracy', 'ManeuverIndicator', 
                'DimensionToStarboard'
              ]

              fieldsToMarkOld.forEach(field => {
                if (enrichedItem.position.data.payload[field] !== undefined) {
                  enrichedItem.position.data.payload[field] = 'OLD'
                }
              })
            }

            // Обновляем время последнего обновления
            enrichedItem.position.data.time = cacheData.position.timestamp
          }
        }

        // Парсим ETA из строки типа "ETA: Oct 05, 23:00  (in 10 days)" для обоих случаев
        if (cacheData.eta_utc && cacheData.eta_utc.includes('ETA:')) {
          try {
            // Извлекаем дату и время из строки ETA
            const etaMatch = cacheData.eta_utc.match(/ETA:\s*([A-Za-z]{3}\s+\d{1,2},\s+\d{1,2}:\d{2})/)
            if (etaMatch) {
              const currentYear = new Date().getFullYear()
              const etaString = `${etaMatch[1]} ${currentYear}`
              const etaDate = new Date(etaString)
              if (!isNaN(etaDate.getTime())) {
                enrichedItem.position.data.eta = etaDate.toISOString()
              }
            }
          } catch (etaError) {
            console.warn(`Error parsing ETA for IMO ${imo}:`, etaError)
          }
        }

        return enrichedItem

      } catch (error) {
        console.error(`Error enriching data for device ${item.deviceId}:`, error)
        return item // Возвращаем оригинальные данные в случае ошибки
      }
    })
  )

  console.log('Fleet data enrichment completed')
  return enrichedData
}

// Загружает AIS данные для корабля по mssId
export async function loadFleetAISData(forceRefresh = false) {
  const { fleetStore } = getStores()

  // Проверяем, есть ли уже данные для этого устройства и не запрошено ли принудительное обновление
  if (!forceRefresh && fleetStore.AISData.length > 0) {
    console.log('Using cached AIS data')
    return fleetStore.AISData
  }

  try {
    const data = await makeApiRequest(`api/v1/fleet/list`, 'get', null, myAxiosRetry)
    
    // Обогащаем данные из локального кэша если флаг включен
    const enrichedData = await enrichFleetDataWithCache(data, AIS_CONFIG.USE_LOCAL_AIS_CACHE)
    
    if (enrichedData && Object.keys(enrichedData).length > 0) {
      enrichedData.forEach(item => {
        const deviceId = item.deviceId

        // ====== ВРЕМЕННАЯ ПОДМЕНА ДАННЫХ - УДАЛИТЬ ПОСЛЕ ТЕСТИРОВАНИЯ ======
        if (deviceId === 'A86F-78B7-5567-06C8') {
          // Подменяем координаты, скорость и курс для тестирования
          if (item.position && item.position.data) {
            item.position.data.lat = 51.75465
            item.position.data.lon = 2.40788
            item.position.data.sog = 9.4
            item.position.data.cog = 224

            // Также обновляем данные в payload, если они есть
            if (item.position.data.payload) {
              // Обновляем координаты в payload.Position
              if (item.position.data.payload.Position) {
                item.position.data.payload.Position.lat = 51.75465
                item.position.data.payload.Position.lon = 2.40788
              }

              // Обновляем скорость в payload.SpeedOverGround
              if (item.position.data.payload.SpeedOverGround) {
                const speedKnots = 9.4
                item.position.data.payload.SpeedOverGround.Knots = speedKnots
                item.position.data.payload.SpeedOverGround.Ms = speedKnots * 0.514444
                item.position.data.payload.SpeedOverGround.Kph = speedKnots * 1.852
                item.position.data.payload.SpeedOverGround.Mph = speedKnots * 1.150779
              }

              // Обновляем курс в payload.CourseOverGround
              item.position.data.payload.CourseOverGround = 224
            }
          }
        }
        // ====== КОНЕЦ ВРЕМЕННОЙ ПОДМЕНЫ ======

        // Добавляем данные AIS для каждого устройства в хранилище (только если есть position.data)
        if (item.position && item.position.data) {
          fleetStore.addAISData(deviceId, item.position.data)
        }
        
        if (item.weather && item.weather.data) {
          fleetStore.addWeatherData(deviceId, item.weather.data)
        }
        
        if (item.activeDashboard && item.activeDashboard.indicators) {
          fleetStore.addAlertsData(deviceId, item.activeDashboard.indicators)
        }
      })
    }

    return enrichedData || data
  } catch (error) {
    console.error(`Failed to load AIS data:`, error)
    return null
  }
}

// Загружает AIS данные для корабля по mssId
// export async function loadFleetAISData_old(deviceId, mssId, forceRefresh = false) {
//     const { fleetStore } = getStores();
//
//     if (!mssId)
//         return
//     // Проверяем, есть ли уже данные для этого устройства и не запрошено ли принудительное обновление
//     if (!forceRefresh && fleetStore.AISData[deviceId]) {
//         console.log("Using cached AIS data for device:", deviceId);
//         return fleetStore.AISData[deviceId];
//     }
//
//     try {
//         const data = await makeApiRequest(`api/v1/mss/vessel/${mssId}/location`, 'get', null, myAxiosRetry);
//         if (data && Object.keys(data).length > 0)
//             fleetStore.addAISData(deviceId, data);
//
//         return data;
//     } catch (error) {
//         console.error(`Failed to load AIS data for device ${deviceId}:`, error);
//         return null;
//     }
// }

// Загружает погоду для корабля по deviceId
export async function loadWeather(deviceId, forceRefresh = false) {
  const { fleetStore } = getStores()

  // Проверяем, есть ли уже данные погоды для этого устройства и не запрошено ли обновление
  if (!forceRefresh && fleetStore.weather && fleetStore.weather[deviceId]) {
    console.log('Using cached weather data for device:', deviceId)
    return fleetStore.weather[deviceId]
  }

  try {
    // Сначала проверяем, есть ли данные AIS с координатами для этого корабя
    const aisData = fleetStore.AISData[deviceId]

    // Если нет данных AIS или отсутствуют координаты, мы не можем получить погоду
    if (!aisData || !aisData.lat || !aisData.lon) {
      console.log('No position data available for weather lookup for device:', deviceId)
      return null
    }

    // Используем текущее время для данных о погоде
    const currentTime = dayjs().unix()

    // Формируем URL для запроса погоды
    const weatherUrl = `https://nogotiles.bgeo.fi:6011/weather_info?lat=${aisData.lat}&lon=${aisData.lon}&t=${currentTime}`

    // Запрашиваем API погоды
    const weatherData = await makeApiRequest(weatherUrl, 'get', null, myAxiosRetry)

    if (weatherData) {
      // Сохраняем данные о погоде в хранилище
      fleetStore.weather = {
        ...fleetStore.weather,
        [deviceId]: {
          ...weatherData,
          timestamp: currentTime,
          coordinates: {
            lat: aisData.lat,
            lon: aisData.lon,
          },
        },
      }

      return fleetStore.weather[deviceId]
    }

    return null
  } catch (error) {
    console.error(`Failed to load weather data for device ${deviceId}:`, error)
    return null
  }
}

// Загружаем AIS данные для всех кораблей и затем отображаем их
export async function loadAllShipsAISDataAndDraw() {
  const { fleetStore } = getStores()
  const ships = fleetStore.getFleet

  if (!ships || ships.length === 0) {
    console.log('No ships available to load AIS data')
    return Promise.resolve()
  }

  console.log('[AIS-DEBUG] Starting to load AIS data for', ships.length, 'ships')

  try {
    const data = await loadFleetAISData()
    console.log('[AIS-DEBUG] AIS data loaded successfully:', data)
    console.log('[AIS-DEBUG] fleetStore.AISData after loading:', fleetStore.AISData)
    return fleetStore.AISData
  } catch (err) {
    console.error(`Error loading AIS data:`, err)
    return null
  }

  // // Создаем массив промисов для загрузки AIS данных для каждого корабля
  // const aisPromises = ships.map(ship =>
  //     loadFleetAISData(ship.ship.deviceId, ship.ship.mssId)
  //         .catch(error => {
  //             console.error(`Error loading AIS data for ${ship.ship.deviceId}:`, error);
  //             return null; // Возвращаем null для неудачных запросов, чтобы Promise.all не завершился с ошибкой
  //         })
  // );
  //
  // // Ждем завершения всех промисов
  // await Promise.all(aisPromises);
  //
  // return fleetStore.AISData;
}

// Загружает транзакции (заказы) для всех кораблей
export async function loadFleetTransactions(forceRefresh = false, isBackground = false) {
  const { fleetStore, momentaryStore } = getStores()

  // Проверяем, есть ли уже данные и не запрошено ли принудительное обновление
  if (!forceRefresh && fleetStore.hasOrders) {
    console.log('Using cached transactions data')
    return fleetStore.transactions
  }

  const loadingKey = 'fleet-transactions'
  const loadingLabel = isBackground ? 'Updating transactions...' : 'Loading transactions...'

  try {
    // Запускаем индикатор загрузки
    if (isBackground) {
      momentaryStore.startBackgroundLoading(loadingKey, loadingLabel)
    } else {
      momentaryStore.startCriticalLoading(loadingKey, loadingLabel)
    }

    const data = await makeApiRequest('api/v1/orders', 'get', null, myAxiosRetry)
    fleetStore.addOrders(data)

    // Останавливаем индикатор загрузки
    if (isBackground) {
      momentaryStore.stopBackgroundLoading(loadingKey)
    } else {
      momentaryStore.stopCriticalLoading(loadingKey)
    }

    return fleetStore.transactions
  } catch (error) {
    // Останавливаем индикатор загрузки в случае ошибки
    if (isBackground) {
      momentaryStore.stopBackgroundLoading(loadingKey)
    } else {
      momentaryStore.stopCriticalLoading(loadingKey)
    }

    console.error('Failed to load fleet transactions:', error)
    throw error
  }
}

// Загружает отгурзки для корабля по deviceId
export async function loadFleetDeliveries(deviceId, period, forceRefresh = false) {
  const { fleetStore } = getStores()

  // Проверяем, есть ли уже данные отгрузок для этого устройства и не запрошено ли принудительное обновление
  if (!forceRefresh && fleetStore.hasDeliveries(deviceId)) {
    console.log('Using cached deliveries data for device:', deviceId)
    return fleetStore.deliveries
  }

  try {
    const requestData = {
      deviceIds: [deviceId],
      dateFrom: period,
    }

    const data = await makeApiRequest('api/v1/deliveries', 'post', requestData)

    let deliveries = []
    if (data.deliveries.length > 0) {
      deliveries = data.deliveries.filter(d => d.deviceId === deviceId)[0]?.deliveries || []
    }

    fleetStore.addDeliveries(deliveries)
    return fleetStore.deliveries
  } catch (error) {
    console.error('Failed to load fleet deliveries:', error)
    return fleetStore.deliveries
  }
}

// Загружает логбук корабля по deviceId
export async function loadFleetLogbook(deviceId, forceRefresh = false) {
  const { fleetStore } = getStores()

  try {
    // Получаем все пользовательские файлы
    const data = await makeApiRequest(`api/v1/userfiles?deviceId=${deviceId}`)
    const { userFiles = [] } = data

    // Находим соответствующие файлы
    const webMessageRecord = userFiles.find(file => file.name === 'record' && file.type === 'logbook_record')

    const ecdisMessageRecord = userFiles.find(file => file.name === 'Update.txt' && file.type === 'message')

    // TODO
    const ecdisRoute = userFiles.find(file => file.name === 'Route.rtz' && file.type === 'route')

    // Создаем массив промисов для обработки данных
    const tasks = []

    if (webMessageRecord) {
      tasks.push(processWebMessages(webMessageRecord.id))
    }

    if (ecdisMessageRecord) {
      tasks.push(processEcdisMessages(ecdisMessageRecord.id, deviceId))
    }

    await Promise.allSettled(tasks)

    return fleetStore
  } catch (error) {
    console.error('Failed to load fleet logbook:', error)
    return fleetStore
  }
}

// Обрабатываем сообщения из логбука
async function processWebMessages(recordId) {
  try {
    const { fleetStore } = getStores()
    const messageData = await makeApiRequest(`api/v1/userfile/${recordId}`)

    fleetStore.addLogbook_web(messageData)
  } catch (error) {
    console.error('Failed to process logbook record:', error)
    return []
  }
}

// Обрабатываем сообщения из ECDIS
async function processEcdisMessages(messageId, deviceId) {
  const { fleetStore } = getStores()

  try {
    const messageData = await makeApiRequest(`api/v1/userfile/${messageId}`)

    const ecdisMessages = messageData.messages.map(message => ({
      source: 'ecdis',
      time: message.timestamp,
      hdg: message?.hdg ?? 0,
      rot: message?.rot ?? 0,
      cog: message?.cog ?? 0,
      sog: message?.sog ?? 0,
      message: message.message,
      pos: {
        lat: message?.lat ?? '',
        lon: message?.lon ?? '',
      },
      images: [],
      audio: {
        src: null,
        blob: null,
      },
      deviceId,
    }))

    fleetStore.addLogbook_ecdis(ecdisMessages)
  } catch (error) {
    console.error('Failed to process ECDIS messages:', error)
  }
}

// Загружает доступные карты по координатам точки
export async function loadMapsByCoordinates(position, providers = ['Primar']) {
  const { fleetStore } = getStores()

  try {
    // Формируем данные для запроса
    const requestData = {
      Points: [
        {
          lat: position.lat,
          lon: position.lon,
        },
      ],
      Providers: providers,
    }

    // Выполняем запрос к API
    const data = await makeApiRequest('api/v1/maps-by-legs', 'post', requestData, myAxiosRetry)

    // Извлекаем названия ячеек из ответа и сортируем их по убыванию
    let cellNames = []

    if (data && data.length > 0) {
      data.forEach(providerData => {
        if (providerData.legsInfo && providerData.legsInfo.length > 0) {
          providerData.legsInfo.forEach(legInfo => {
            if (legInfo.mapIntersections && legInfo.mapIntersections.length > 0) {
              // Извлекаем id ячеек и добавляем их в массив
              const ids = legInfo.mapIntersections.map(intersection => intersection.id)
              cellNames = [...cellNames, ...ids]
            }
          })
        }
      })

      // Удаляем дубликаты и сортируем по убыванию
      cellNames = [...new Set(cellNames)].sort((a, b) => b.localeCompare(a))
    }

    return cellNames
  } catch (error) {
    console.error('Failed to load maps by coordinates:', error)
    return []
  }
}

// Проверяет наличие самой детальной карты для текущей позиции корабля
export async function checkShipFolioForPosition(deviceId) {
  const { fleetStore } = getStores()

  try {
    // Получаем AIS данные корабля с его текущей позицией
    const aisData = fleetStore.AISData[deviceId]

    // Если нет данных о позиции, выходим
    if (!aisData || !aisData.payload || !aisData.payload.Position) {
      console.log(`No position data for deviceId: ${deviceId}`)
      fleetStore.setFolioAlerts(deviceId, { hasAlert: false, charts: [] })
      return false
    }

    // Получаем координаты из данных AIS
    const position = aisData.payload.Position

    // Получаем список доступных карт для текущей позиции
    const availableCharts = await loadMapsByCoordinates(position)

    // Если нет доступных карт, выходим
    if (!availableCharts || availableCharts.length === 0) {
      console.log(`No available charts for deviceId: ${deviceId}`)
      fleetStore.setFolioAlerts(deviceId, { hasAlert: false, charts: [] })
      return false
    }

    // Находим самую детальную карту (с самой большой цифрой в 3-й позиции)
    let mostDetailedChart = availableCharts[0]
    for (const chart of availableCharts) {
      if (chart.length >= 3 && chart.charCodeAt(2) > mostDetailedChart.charCodeAt(2)) {
        mostDetailedChart = chart
      }
    }

    // Загружаем фолио для корабля, если оно еще не загружено
    if (!fleetStore.folio || fleetStore.folio.length === 0) {
      await loadFleetFolio(deviceId, true)
    }

    // Фильтруем купленные карты для этого корабля
    const paidItems = fleetStore.folio.filter(item => item.deviceId === deviceId && item.isPaid === true && item.status === 'complete' && !isExpired(item.expireAt))

    // Проверяем, есть ли самая детальная карта среди купленных и не истек ли срок
    let hasDetailedChart = false
    if (paidItems.length > 0) {
      for (const item of paidItems) {
        if (item.items && item.items.length > 0) {
          // Проверяем, содержит ли фолио самую детальную карту
          const hasChart = item.items.some(
            chartItem =>
              // chartItem.type === 'S63 maps' &&
              chartItem.id === mostDetailedChart
          )

          if (hasChart) {
            hasDetailedChart = true
            break
          }
        }
      }
    }

    // Сохраняем результат проверки и список доступных карт
    fleetStore.setFolioAlerts(deviceId, {
      hasAlert: !hasDetailedChart,
      charts: availableCharts,
      mostDetailed: mostDetailedChart,
    })

    return !hasDetailedChart // Возвращаем true если нужна карта (есть алерт)
  } catch (error) {
    console.error(`Error checking folio for deviceId ${deviceId}:`, error)
    return false
  }
}

// Проверяет, истек ли срок действия карты
// TODO
function isExpired(expireDate) {
  // if (!expireDate) return true;
  // const now = new Date();
  // const expire = new Date(expireDate);
  // return now > expire;

  return false
}

// Проверяет наличие карт для всех кораблей с позициями
export async function checkAllShipsFolios() {
  const { fleetStore } = getStores()
  const ships = fleetStore.getFleet

  if (!ships || ships.length === 0) {
    console.log(`No available ships for checking charts`)
    return
  }

  // Фильтруем корабли, у которых есть AIS данные с позицией
  const shipsWithPosition = ships.filter(ship => {
    const deviceId = ship.ship.deviceId
    const aisData = fleetStore.AISData[deviceId]
    return aisData && aisData.payload && aisData.payload.Position
  })

  // Создаем массив промисов для проверки фолио каждого корабля
  const folioCheckPromises = shipsWithPosition.map(ship =>
    checkShipFolioForPosition(ship.ship.deviceId).catch(error => {
      console.error(`Error checking folio for ${ship.ship.deviceId}:`, error)
      return false
    })
  )

  // Ждем завершения проверки фолио для всех кораблей
  await Promise.all(folioCheckPromises)
  console.log(`Folio check completed for all ships`)
}

// Загружает данные AIO (Admiralty Information Overlay) из локального файла
export async function loadAIO() {
  const { fleetStore } = getStores()

  if (!fleetStore.hasAIO) {
    try {
      // Загружаем данные AIO из файла
      let aioData = await loadJSON('aio.json')
      if (!aioData) {
        throw new Error('Некорректный формат данных ADMIRALTY Information Overlay')
      }

      // Извлекаем временные и постоянные предупреждения
      let tempData = aioData.TempInfos ? aioData.TempInfos : []
      let permanentData = aioData.PermanentInfos ? aioData.PermanentInfos : []

      // Сохраняем данные в хранилище Pinia
      fleetStore.addAIO(tempData, permanentData)
      console.log('AIO data loaded successfully', { temp: tempData.length, permanent: permanentData.length })
      return
    } catch (error) {
      console.error('Error loading AIO data:', error)
      throw error
    }
  }
}

// Загружает данные Navarea из локального файла
export async function loadNavarea() {
  const { fleetStore } = getStores()

  if (!fleetStore.hasNavarea) {
    try {
      // Загружаем данные Navarea из файла
      let navareaData = await loadJSON('navarea.json')
      if (!navareaData) {
        throw new Error('Некорректный формат данных Navarea')
      }

      // Сохраняем данные в хранилище Pinia
      fleetStore.addNavarea(navareaData)
      console.log('Navarea data loaded successfully', { count: navareaData.length })
      return
    } catch (error) {
      console.error('Error loading Navarea data:', error)
      throw error
    }
  }
}

// Загружает данные портов из локального файла
export async function loadPorts() {
  const { fleetStore } = getStores()

  if (!fleetStore.hasPorts) {
    try {
      // Загружаем данные ports из файла
      let portsData = await loadJSON('ports.json')
      if (!portsData) {
        throw new Error('Некорректный формат данных ports')
      }

      // Сохраняем данные в хранилище Pinia
      fleetStore.addPorts(portsData)
      console.log('Ports data loaded successfully', { count: portsData.length })
      return
    } catch (error) {
      console.error('Error loading ports data:', error)
      throw error
    }
  }
}
